import {inject} from "../di";
import {
	IAnalyticsService, CouponActions, CouponSource, LoginSource,
	LoyaltyCardSource, ShoppingListAction, ShoppingListEventContext, FamilySyncAction,
	FavouriteAction, FavouriteContext, LandingPageActions, CrossBrowseAction, AggregateSourceAction,
	FlyerPlacement, Dimension, LANDING_PAGE_VERSION, TrackFlippProps
} from "../definitions/IAnalyticsService";
import {BaseAnalyticsDelegate, AnalyticsParameters, LANDING_PAGE_ACTIONS} from "./BaseAnalyticsDelegate";
import {ConfigService} from "../ConfigService";
import {EVTrackingService} from "../EVTrackingService";
import {Flyer} from "../../definitions/FlyerDefinitions";
import {Coupon} from "../../definitions/CouponDefinitions";
import {SearchMode, LoyaltyProgram, Experiment, LoyaltyProgramCoupon, LoyaltyProgramMerchant,
				AuthProvider} from "../../definitions/CommonDefinitions";

export class DefaultAnalyticsDelegate extends BaseAnalyticsDelegate implements IAnalyticsService {

	@inject config: ConfigService;
	@inject evTracker: EVTrackingService;

	protected _analyticsParameters: AnalyticsParameters = {};

	constructor() {
		super();
		this.setSessionIdInGTM();
		this._buildAnalyticsParameters();
	}

	analyticsParameters() {
		const params: { [key: string]: string } = {};
		Object.getOwnPropertyNames(this._analyticsParameters).forEach((key) => {
			params[key] = this._analyticsParameters[key];
		});

		params["postal_code"] = this.postalCode;
		params["channel_id"] = this.channelId.toString();
		params["rnd"] = Math.random().toString().slice(2);
		params["app_version"] = this.config.BUILD;
		params["account_guid"] = this._accountGuid || this.sessionId;
		return params;
	}

	isAnalyticsDelayed() {
		return false;
	}

	get channelId(): number {
		if (this._analyticsParameters["channel_id"]) {
			return Number(this._analyticsParameters["channel_id"]);
		} else {
			return this.postalCode.length > 5 ? this.config.FLIPP_CANADA_CHANNEL_ID : this.config.FLIPP_US_CHANNEL_ID;
		}
	}

	getTrackFlyerOpenPayload(flyer: Flyer, xbrowseId: string, sourceAction: AggregateSourceAction) {
		const id = flyer.id.toString();
		return this.combineFlyerAnalytics(flyer, {
			flyer_id: id,
			flyer_run_id: flyer.flyerRunId.toString(),
			flyer_type_id: flyer.flyerTypeId.toString(),
			merchant_id: flyer.merchantId.toString(),
			flipp_premium_merchant: flyer.premium ? "1" : "0",
			repeat: this.evTracker.hasEngaged(flyer.id, "open") ? "1" : "0",
			crossbrowse: xbrowseId,
			source_action: sourceAction
		});
	}

	trackFlyerOpen(flyer: Flyer, sourceAction: AggregateSourceAction) {
		const xbrowseId = this.getXBrowseOpen();

		super.trackFlyerOpen(flyer, sourceAction, xbrowseId);

		const payload = this.getTrackFlyerOpenPayload(flyer, xbrowseId, sourceAction);

		const trackFlippProps: TrackFlippProps = {
			ev: "open",
			options: payload
		};
		this.trackFlipp(trackFlippProps);
		this.trackTag("open");

		this.evTracker.setEngagement(flyer.id, "open");
	}

	getTrackFlyerEngagementPayload(flyer: Flyer, xbrowseId: string, sourceAction: AggregateSourceAction) {
		const id = flyer.id.toString();
		const repeat = this.evTracker.hasEngaged(flyer.id, "ev");

		return this.combineFlyerAnalytics(flyer, {
			flyer_id: id,
			flyer_run_id: flyer.flyerRunId.toString(),
			flyer_type_id: flyer.flyerTypeId.toString(),
			merchant_id: flyer.merchantId.toString(),
			flipp_premium_merchant: flyer.premium ? "1" : "0",
			repeat: repeat ? "1" : "0",
			merchant: flyer.merchant,
			crossbrowse: xbrowseId,
			source_action: sourceAction
		});
	}

	trackFlyerEngagement(flyer: Flyer, sourceAction: AggregateSourceAction) {
		const xbrowseId = this.getXBrowseEngagement();
		const status = this.evTracker.engagementStatus(flyer);

		super.trackFlyerEngagement(flyer, sourceAction, xbrowseId);

		const payload = this.getTrackFlyerEngagementPayload(flyer, xbrowseId, sourceAction);
		const trackFlippProps: TrackFlippProps = {
			ev: "ev",
			options: payload
		};
		this.trackFlipp(trackFlippProps);
		if (status === "billable")
			this.trackTag("ev");

		this.evTracker.setEngagement(flyer.id, "ev");
	}

	getTrackFlyerViewPayload(
		flyer: Flyer,
		left: number,
		top: number,
		width: number,
		height: number,
		zoomScale: number
	) {
		return {
			ev: "view",
			options: {
				flyer_id: flyer.id.toString(),
				flyer_run_id: flyer.flyerRunId.toString(),
				flyer_type_id: flyer.flyerTypeId.toString(),
				merchant_id: flyer.merchantId.toString(),
				flipp_premium_merchant: flyer.premium ? "1" : "0",
				left: left.toString(),
				top: (-top).toString(),
				right: (left + width).toString(),
				bottom: (-(top + height)).toString(),
				resolution: (1 / zoomScale).toString(),
				userScreenHeight: window.screen.availHeight.toString(),
				userScreenWidth: window.screen.availWidth.toString()
			}
		};
	}


	trackFlyerView(
		flyer: Flyer, left: number, top: number, width: number,
		height: number, zoomScale: number, interaction: number) {

		super.trackFlyerView(flyer, left, top, width, height, zoomScale, interaction);
		// The "view" event coordinate system is *weird*, internally we use
		// standard top,left screen coordinates, where moving down is *increasing*
		// the "top" coordinate, and moving right is *increasing* the left
		// coordinate.
		// NOTE: The "left" and "top" parameters passed into this function,
		// describe the top, left origin of the viewport *on the flyer*.
		// ```
		//   0,0
		//    *-------
		//    |
		//    | (3, 3)   (3, 13)
		//    |   *--------*
		//    |   |viewport|
		//    |   |        |
		//    |   *--------*
		//    | (6, 3)   (6, 13)
		// ```
		// The "view" event expects top,left cartesian coordinates. So in the
		// above example, the (top, left, bottom, right) is (-3, 3, -6, 13)
		// respectively.
		const id = flyer.id.toString();
		const trackFlippProps: TrackFlippProps = this.getTrackFlyerViewPayload(flyer, left, top, width, height, zoomScale);
		this.trackFlipp(trackFlippProps);

		this.trackGA("send", {
			hitType: "event",
			eventCategory: "Flyer",
			eventAction: "view",
			eventLabel: flyer.merchant,
			eventValue: interaction,
			[Dimension.FlyerStatus.toString()]: this.evTracker.engagementStatus(flyer),
			[Dimension.MerchantName.toString()]: flyer.merchant,
			[Dimension.MerchantId.toString()]: flyer.merchantId.toString(),
			[Dimension.FlyerId.toString()]: id,
		});
	}

	getTrackFlyerItemPayload(flyer: Flyer, itemId: number, st: number) {
		const id = flyer.id.toString();
		return {
			flyer_id: id,
			flyer_run_id: flyer.flyerRunId.toString(),
			flyer_type_id: flyer.flyerTypeId.toString(),
			flyer_item_id: itemId.toString(),
			flipp_premium_merchant: flyer.premium ? "1" : "0",
			merchant_id: flyer.merchantId.toString(),
			st: st.toString(),
			item_id: itemId.toString(),
			item_type: "flyer",
			is_hidden: "0"
		};
	}

	// NOTE: when the item is of type flyer, both item_id and flyer_item_id will be the same (for now)
	trackFlyerItem(flyer: Flyer, itemId: number, st: number) {
		super.trackFlyerItem(flyer, itemId, st);

		const trackFlippProps: TrackFlippProps = {
			ev: "item",
			options: this.getTrackFlyerItemPayload(flyer, itemId, st)
		};
		this.trackFlipp(trackFlippProps);
	}

	trackEcomItem(itemId: number, merchantId: number, merchant: string, st: number, isHidden: boolean) {
		super.trackEcomItem(itemId, merchantId, merchant, st, isHidden);
		const trackFlippProps: TrackFlippProps = {
			ev: "item",
			options: {
				flyer_id: "",
				flyer_run_id: "",
				flyer_type_id: "",
				flyer_item_id: "",
				flipp_premium_merchant: "",
				merchant_id: merchantId.toString(),
				st: st.toString(),
				item_id: itemId.toString(),
				item_type: "ecom",
				is_hidden: isHidden ? "1" : "0"
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackSearch(q: string, flyerCount: number, tombstoneCount: number, itemCount: number, ecomItemCount: number, couponCount: number, couponMatchupCount: number, mode: SearchMode) {
		super.trackSearch(q, flyerCount, tombstoneCount, itemCount, ecomItemCount, couponCount, couponMatchupCount, mode);
		const count = (flyerCount + itemCount + ecomItemCount).toString();

		// sanitized query, follow what app does:
		// 10 apples = apples, 10lb potato = 10lb potato,
		// 2 new york strip steak = new york strip steak
		const sanitizedQuery = q.replace(/^([0-9]+ )/, "");

		const trackFlippProps: TrackFlippProps = {
			ev: "searched",
			options: {
				q: sanitizedQuery,
				q_raw: q,
				flyer_count: flyerCount.toString(),
				item_count: itemCount.toString(),
				ecom_count: ecomItemCount.toString(),
				coupon_count: `${couponCount}`,
				merchant_page_count: `${tombstoneCount}`,
				flyer_match_count: "0", // Always 0 for now - we don't distinguish matchups on flyer items
				coupon_match_count: `${couponMatchupCount}`,
				count: count,
				mode: mode
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackTombstone(q: string, merchant: string, merchantId: number) {
		super.trackTombstone(q, merchant, merchantId);
		// sanitized query, follow what app does:
		// 10 apples = apples, 10lb potato = 10lb potato,
		// 2 new york strip steak = new york strip steak
		const sanitizedQuery = q.replace(/^([0-9]+ )/, "");

		const trackFlippProps: TrackFlippProps = {
			ev: "merchant_page",
			options: {
				q: sanitizedQuery,
				q_raw: q,
				merchant_id: merchantId.toString(),
				merchant: merchant
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackSearchItemClick(q: string, itemId: number, flyer: Flyer, rank: number) {
		super.trackSearchItemClick(q, itemId, flyer, rank);
		const id = flyer.id.toString();

		// sanitized query, follow what app does:
		// 10 apples = apples, 10lb potato = 10lb potato,
		// 2 new york strip steak = new york strip steak
		const sanitizedQuery = q.replace(/^([0-9]+ )/, "");

		const trackFlippProps: TrackFlippProps = {
			ev: "search_hit",
			options: {
				flyer_id: id,
				flyer_run_id: flyer.flyerRunId.toString(),
				flyer_type_id: flyer.flyerTypeId.toString(),
				flyer_item_id: itemId.toString(),
				merchant_id: flyer.merchantId.toString(),
				item_id: itemId.toString(), // NOTE: when the item is of type flyer, both item_id and flyer_item_id will be the same (for now)
				q: sanitizedQuery,
				q_raw: q,
				rank: rank.toString(),
				item_type: "flyer",
				is_hidden: "0"
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackSearchFlyerClick(q: string, flyer: Flyer, rank: number) {
		super.trackSearchFlyerClick(q, flyer, rank);
		const id = flyer.id.toString();

		// sanitized query, follow what app does:
		// 10 apples = apples, 10lb potato = 10lb potato,
		// 2 new york strip steak = new york strip steak
		const sanitizedQuery = q.replace(/^([0-9]+ )/, "");

		const trackFlippProps: TrackFlippProps = {
			ev: "search_hit",
			options: {
				flyer_id: id,
				flyer_run_id: flyer.flyerRunId.toString(),
				flyer_type_id: flyer.flyerTypeId.toString(),
				flyer_item_id: null,
				merchant_id: flyer.merchantId.toString(),
				item_id: null,
				q: sanitizedQuery,
				q_raw: q,
				rank: rank.toString(),
				item_type: "merchant_flyer",
				is_hidden: "0"
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackSearchCouponClick(q: string, coupon: Coupon, rank: number) {
		super.trackSearchCouponClick(q, coupon, rank);
		const sanitizedQuery = q.replace(/^([0-9]+ )/, "");

		const trackFlippProps: TrackFlippProps = {
			ev: "search_hit",
			options: {
				flyer_id: null,
				flyer_run_id: null,
				flyer_type_id: null,
				flyer_item_id: null,
				merchant_id: null,
				coupon_id: `${coupon.id}`,
				q: sanitizedQuery,
				q_raw: q,
				rank: rank.toString(),
				item_type: "coupon",
				is_hidden: "0"
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackSearchEcomItemClick(q: string, itemId: number, merchantId: number, isHidden: boolean, rank: number) {
		super.trackSearchEcomItemClick(q, itemId, merchantId, isHidden, rank);

		// sanitized query, follow what app does:
		// 10 apples = apples, 10lb potato = 10lb potato,
		// 2 new york strip steak = new york strip steak
		const sanitizedQuery = q.replace(/^([0-9]+ )/, "");

		const trackFlippProps: TrackFlippProps = {
			ev: "search_hit",
			options: {
				flyer_id: "",
				flyer_run_id: "",
				flyer_type_id: "",
				flyer_item_id: "",
				merchant_id: merchantId.toString(),
				item_id: itemId.toString(),
				q: sanitizedQuery,
				q_raw: q,
				rank: rank.toString(),
				item_type: "ecom",
				is_hidden: isHidden ? "1" : "0"
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackEcomItemTTMClick(itemId: number, merchantId: number) {
		super.trackEcomItemTTMClick(itemId, merchantId);

		const trackFlippProps: TrackFlippProps = {
			ev: "item_click_ecom",
			options: {
				item_id: itemId.toString(),
				merchant_id: merchantId.toString()
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackCrossBrowse(type: CrossBrowseAction, from?: Flyer, to?: Flyer, idx?: number) {
		super.trackCrossBrowse(type, from, to, idx);
	}

	trackCoupon(
		coupon: Coupon, st: CouponActions, src: CouponSource, flyerId?: number,
		programMerchant?: LoyaltyProgramMerchant, loyaltyCoupon?: LoyaltyProgramCoupon) {

		super.trackCoupon(coupon, st, src, flyerId, programMerchant, loyaltyCoupon);
		const merchantId = programMerchant ?
			String(programMerchant.merchantId) : coupon.merchantId ?
			String(coupon.merchantId) : "";
		const trackFlippProps: TrackFlippProps = {
			ev: "coupon",
			options: {
				st: String(st),
				coupon_id: String(coupon.id),
				merchant_id: String(merchantId),
				coupon_vendor_id: String(coupon.vendorId),
				coupon_type: coupon.type ? coupon.type.toString() : null,
				brand: coupon.brand,
				flyer_id: flyerId ? `${flyerId}` : null,
				redemption_method: coupon.redemptionMethod.toString(),
				click_source: src.toString(),
				loyalty_program_id: loyaltyCoupon ? String(loyaltyCoupon.loyaltyProgramId) : null,
				loyalty_program_coupon_id: loyaltyCoupon ? String (loyaltyCoupon.id) : null
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackVariant(experiment: Experiment, value: string) {
		super.trackVariant(experiment, value);
		const trackFlippProps: TrackFlippProps = {
			ev: "experiment",
			options: {
				experiment: experiment,
				variant: value
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackSignin(userGuid: string, provider: AuthProvider, context: LoginSource) {
		super.trackSignin(userGuid, provider, context);
		const accounts = ["google", "facebook"];
		const trackFlippProps: TrackFlippProps = {
			ev: "login",
			options: {
				st: accounts[provider],
				context: context.toString()
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackSignout(userGuid: string, provider: AuthProvider) {
		super.trackSignout(userGuid, provider);
		const accounts = ["google", "facebook"];
		const trackFlippProps: TrackFlippProps = {
			ev: "logout",
			options: {
				st: accounts[provider]
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackLoyaltyCardAdded(program: LoyaltyProgram, context: LoyaltyCardSource) {
		super.trackLoyaltyCardAdded(program, context);
		const sourceMap: {[key: number]: string} = {
			[LoyaltyCardSource.CouponDetails]: "coupon_details",
			[LoyaltyCardSource.FlyerItem]: "flyer_item",
			[LoyaltyCardSource.CouponPage]: "coupon_page",
		};

		const trackFlippProps: TrackFlippProps = {
			ev: "add_card",
			options: {
				context: sourceMap[context],
				loyalty_program_id: String(program.id),
				type: program.validationMethod,
				barcode: null
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackLoyaltyCardRemoved(programId: number) {
		super.trackLoyaltyCardRemoved(programId);

		const trackFlippProps: TrackFlippProps = {
			ev: "remove_card",
			options: {
				loyalty_program_id: String(programId),
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackShoppingListChanged(
		item: string, listId: string,
		action: ShoppingListAction, context: ShoppingListEventContext, numClippings?: string) {

		let qty: string = null;
		let sanitizedItem: string = item ? item.trim() : item;

		if (item) {
			// Strip out the leading number and trim any space around the item match.
			// "apples" --> null and "apples"
			// "apples    " --> null and "apples"
			// "2apples" --> "2" and "apples"
			// "2 apples" --> "2" and "apples"
			// "2lbs apples" --> "2" and "lbs apples"
			// "   2 lbs apples  "  --> "2" and "lbs apples"
			const match = item.match(/^\s*(\d+)\s*(\w.*)$/);

			if (match && match[1] && match[2]) {
				qty = match[1];
				sanitizedItem = match[2].trim();
			}
		}

		super.trackShoppingListChanged(item, listId, action, context);

		const payload: {[key: string]: string} = {
			st: action.toString(),
			shopping_list_id: listId || "local",
			item: sanitizedItem,
			qty,
			context: context.toString(),
			in_aisle_mode: null
		};

		if (action === ShoppingListAction.Check || action === ShoppingListAction.Uncheck) {
			payload["aisle_category"] = null;
			payload["clip"] = numClippings;
		} else if (action === ShoppingListAction.Delete) {
			payload["clip"] = numClippings;
		}

		const trackFlippProps: TrackFlippProps = {
			ev: "shopping_list",
			options: payload
		};
		this.trackFlipp(trackFlippProps);
	}

	trackShoppingListPhotoDeleted(listId: string) {
		super.trackShoppingListPhotoDeleted(listId);
		const trackFlippProps: TrackFlippProps = {
			ev: "photo_sl_delete",
			options: {
				n_items: "1",
				shopping_list_id: listId || "local"
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackShoppingListRecommendedStream(
		action: string, stream: string, listId: string, item: string, canonicalCategory: string) {

		super.trackShoppingListRecommendedStream(action, stream, listId, item, canonicalCategory);
		const trackFlippProps: TrackFlippProps = {
			ev: "shopping_list",
			options: {
				st: action,
				stream,
				item,
				canonical_category: canonicalCategory,
				in_aisle_mode: null
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackSearchMiss(item: string, listId: string) {
		super.trackSearchMiss(item, listId);
		const trackFlippProps: TrackFlippProps = {
			ev: "shopping_list",
			options: {
				st: "searchMiss",
				shopping_list_id: listId || "",
				q: item,
				hits: `0`
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackAutocompleteHit(q: string, term: string, rank: number, context: string) {
		super.trackAutocompleteHit(q, term, rank, context);
		const trackFlippProps: TrackFlippProps = {
			ev: "autocomplete_click",
			options: {
				q,
				autocomplete_term: term,
				rank: rank.toString(),
				view: context
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackShoppingListPrint(listId: string, action: ShoppingListAction) {
		super.trackShoppingListPrint(listId, action);
		const trackFlippProps: TrackFlippProps = {
			ev: "shopping_list",
			options: {
				st: action.toString(),
				shopping_list_id: listId || "local"
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackShoppingListEvent(listId: string, action: ShoppingListAction, numAffected: number) {
		super.trackShoppingListEvent(listId, action, numAffected);
		const trackFlippProps: TrackFlippProps = {
			ev: "shopping_list",
			options: {
				st: action.toString(),
				item: null,
				qty: null,
				clip: null,
				clear: numAffected.toString(),
				context: "list",
				in_aisle_mode: null,
				shopping_list_id: listId || "local"
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackFamilySyncEvent(listId: string, action: FamilySyncAction, inviteId?: string) {
		super.trackFamilySyncEvent(listId, action, inviteId);
		const payload: {[key: string]: string} = {
			st: action.toString()
		};
		if (listId) {
			payload["shopping_list_id"] = listId;
		}
		if (inviteId) {
			payload["invite_id"] = inviteId;
		}
		const trackFlippProps: TrackFlippProps = {
			ev: "shopping_list",
			options: payload
		};
		this.trackFlipp(trackFlippProps);
	}

	trackFavouriteEvent(merchantId: number, action: FavouriteAction, context: FavouriteContext) {
		super.trackFavouriteEvent(merchantId, action, context);
		const trackFlippProps: TrackFlippProps = {
			ev: "favorite",
			options: {
				action,
				merchant: `${merchantId}`,
				view: context.toString()
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackSearchFiltering(count: number, sortOrder: string) {
		super.trackSearchFiltering(count, sortOrder);
		const trackFlippProps: TrackFlippProps = {
			ev: "search_filter",
			options: {
				filter: sortOrder,
				count: count.toString()
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackAdditionalResultsClick(count: number) {
		super.trackAdditionalResultsClick(count);
		const trackFlippProps: TrackFlippProps = {
			ev: "show_more_ecom",
			options: { count: count.toString() }
		};
		this.trackFlipp(trackFlippProps);
	}

	trackFlyerListing(categoryName: string, flyers: Flyer[], sortOrder: string, favMerchants?: number[]) {
		super.trackFlyerListing(categoryName, flyers, sortOrder, favMerchants);
		const flyerIds: string[] = this.generateFlyerIds(categoryName, flyers, favMerchants);
		const screenName: string = (categoryName === "favorites")
			? categoryName
			: "featured-".concat(categoryName);

		const trackFlippProps: TrackFlippProps = {
			ev: "listing",
			options: {
				screen_name: "flyers",
				flyer_count: flyerIds.length.toString(),
				sort: sortOrder,
				flyer_ids: flyerIds.join(""),
				category: screenName
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackFlyerClick(category: string, sort: string, placement: FlyerPlacement, position: number) {
		super.trackFlyerClick(category, sort, placement, position);
		const trackFlippProps: TrackFlippProps = {
			ev: "flyer_click",
			options: {
				Category: category,
				Sort: sort,
				Placement: `${placement.toString()}`,
				Position: `${position}`
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackCouponListing(categoryName: string, coupons: Coupon[], sortOrder: string) {

		/* We use "all", "savetocard", "print" for coupon filters. Need to update this to
		 * per the beacon spec*/
		let sortValue = null;
		if (sortOrder === "savetocard") {
			sortValue = "load_to_card";
		} else if (sortOrder === "print") {
			sortValue = "print";
		} else {
			sortValue = "all";
		}

		super.trackCouponListing(categoryName, coupons, sortOrder);
		const trackFlippProps: TrackFlippProps  = {
			ev: "listing",
			options: {
				screen_name: "coupons",
				campaign_count: null,
				loyalty_programs_count: null,
				sort: sortValue,
				category_count: `${coupons.length}`,
				loyalty_program: null,
				loyalty_program_id: null
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackLandingPage(st: LandingPageActions, q?: string, flyer?: Flyer) {
		super.trackLandingPage(st, q, flyer);
		const query: string = q ? q : "";

		const trackFlippProps: TrackFlippProps = {
			ev: "landing_page_click",
			options: {
				st: LANDING_PAGE_ACTIONS[st],
				flyer_id: flyer ? `${flyer.id}` : "",
				flyer_run_id: flyer ? `${flyer.flyerRunId}` : "",
				flyer_type_id: flyer ? `${flyer.flyerTypeId}` : "",
				merchant_id: flyer ? `${flyer.merchantId}` : "",
				flipp_premium_merchant: flyer && flyer.premium ? "1" : "0",
				q: query,
				version: LANDING_PAGE_VERSION
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackLocationChanges(oldFsa: string, newFsa: string) {
		super.trackLocationChanges(oldFsa, newFsa);
		const trackFlippProps: TrackFlippProps = {
			ev: "location_changed",
			options: {
				old_location: oldFsa,
				new_location: newFsa
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackShoppingListView(items: number, photos: number, flyerItems: number, ecomItems: number, coupon: number) {
		const trackFlippProps: TrackFlippProps = {
			ev: "listing",
			options: {
				screen_name: "shopping_list",
				photo_count: `${photos}`,
				shopping_list_size: `${items}`,
				clippings_count: `${flyerItems + ecomItems}`,
				coupon_count: `${coupon}`
			}
		};
		this.trackFlipp(trackFlippProps);
	}

	trackFlyerClose(flyer: Flyer, sourceAction: AggregateSourceAction) {}

	trackWindowClose() {}

	protected _buildAnalyticsParameters() {
		this._analyticsParameters = {
			aid: "flipp",
			device_platform: "Web",
			sid: this.sessionId,
		};
	}
}
